import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '../../../../shared/Theme/theme';
import { bpActivityFeedItem } from '../../../../../models/entities/businessPartner/bpActivityFeed';
import { userInfo } from '../../../../../models/entities/user';
import { localSDK as client } from '../../../../../sdk';
import Loader from '../../../../shared/SmallComponents/Loader';

type Props = {
    businessPartnerId: string;
    isActive: boolean;
    users?: userInfo[];
};

/** End-user copy when we cannot match a profile in this screen’s user list (still tied to a user id). */
const ACTOR_SOMEONE_ON_TEAM = 'Someone on your team';
/**
 * Activity from imports, legacy data, or jobs that did not record a specific user.
 * We intentionally do not echo raw `actorName` here so technical labels never reach customers.
 */
const ACTOR_EARLIER_ACTIVITY = 'Earlier activity';

const displayNameForUser = (u: userInfo): string => {
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    if (full) return full;
    if (u.email?.trim()) return u.email.trim();
    if (u.username?.trim()) return u.username.trim();
    return '';
};

const actorLabel = (row: bpActivityFeedItem, usersById: Map<string, userInfo>): string => {
    const rawName = row.actorName?.trim();

    if (row.actorUserId) {
        const u = usersById.get(row.actorUserId);
        const fromProfile = u ? displayNameForUser(u) : '';
        if (fromProfile) return fromProfile;
        if (rawName) return rawName;
        return ACTOR_SOMEONE_ON_TEAM;
    }

    if (rawName) return ACTOR_EARLIER_ACTIVITY;

    return '—';
};

const sourceLabel = (source: bpActivityFeedItem['source']): string => {
    switch (source) {
        case 'audit':
            return 'Audit';
        case 'discussion':
            return 'Note';
        case 'document':
            return 'Document';
        case 'payment':
            return 'Payment';
        default:
            return source;
    }
};

const formatWhen = (iso: string): string => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
};

const ActivityLogTab: React.FC<Props> = ({ businessPartnerId, isActive, users = [] }) => {
    const [items, setItems] = useState<bpActivityFeedItem[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const usersById = useMemo(() => {
        const m = new Map<string, userInfo>();
        for (const u of users) {
            if (u?.id) m.set(u.id, u);
        }
        return m;
    }, [users]);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await client.businessPartners().fetchActivityFeed(businessPartnerId);
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load activity');
            setItems([]);
        }
    }, [businessPartnerId]);

    useEffect(() => {
        if (!isActive || !businessPartnerId) return;
        setItems(null);
        void load();
    }, [isActive, businessPartnerId, load]);

    if (!isActive) return null;

    if (items === null) {
        return (
            <Centered>
                <Loader marginTop="32px" />
            </Centered>
        );
    }

    return (
        <Wrap>
            {error ? <ErrorBanner>{error}</ErrorBanner> : null}
            {items.length === 0 ? (
                <Empty>No activity recorded yet.</Empty>
            ) : (
                <List>
                    {items.map((row) => (
                        <Row key={row.id}>
                            <RowTop>
                                <Meta>
                                    <When>{formatWhen(row.occurredAt)}</When>
                                    <Badge data-source={row.source}>{sourceLabel(row.source)}</Badge>
                                </Meta>
                                <Actor>{actorLabel(row, usersById)}</Actor>
                            </RowTop>
                            <Title>{row.title}</Title>
                            {row.detail ? <Detail>{row.detail}</Detail> : null}
                        </Row>
                    ))}
                </List>
            )}
        </Wrap>
    );
};

const Wrap = styled.div`
    padding: 8px 12px 24px;
    max-width: 840px;
`;

const Centered = styled.div`
    display: flex;
    justify-content: center;
    padding: 40px;
`;

const ErrorBanner = styled.div`
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 13px;
`;

const Empty = styled.p`
    margin: 32px 0;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
`;

const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const Row = styled.li`
    padding: 14px 0;
    border-bottom: 1px solid #eef0f3;
    &:last-child {
        border-bottom: none;
    }
`;

const RowTop = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
`;

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const When = styled.span`
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
`;

const Badge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    &[data-source='audit'] {
        background: #eef2ff;
        color: #4338ca;
    }
    &[data-source='discussion'] {
        background: #ecfdf5;
        color: #047857;
    }
    &[data-source='document'] {
        background: #fff7ed;
        color: #c2410c;
    }
    &[data-source='payment'] {
        background: #e0f2fe;
        color: #0369a1;
    }
`;

const Actor = styled.span`
    font-size: 12px;
    color: #475467;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
`;

const Detail = styled.div`
    margin-top: 6px;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.45;
    white-space: pre-wrap;
`;

export default ActivityLogTab;
